// The Model tab: which model answers for the agent. A project agent asks for a kind of model (a role: fast,
// standard, reasoning) and the project's AI settings say which model each kind is; the tab shows one card per
// distinct model, naming the kinds it covers, and a project agent's pick is saved here (one commit). A card of
// two kinds keeps the agent's kind when it is one of them. Below: where the models are set (the project's AI
// settings), and — once another model is picked and the agent has evaluations — a run of them with it first.
// A platform agent's cards are read-only (a project cannot change its model): its own kind marked, or — when it
// has none — the chat's default, as the person picks the model in the chat.
import { cn } from '@cortex/ui';
import { Loader2 } from 'lucide-react';
import * as React from 'react';
import { Link } from 'react-router';
import { evalsKey } from './Evaluations.tsx';
import { useAgentsHost, useAgentsQuery, useInvalidate, useAgentsKey } from './host.tsx';
import { MODEL_ROLES, roleLabel, type ModelRole } from './model.ts';
import type { AgentTabContext } from './tabs.tsx';
import { btnGhost, btnPrimary, messageOf } from './ui.tsx';

const SET_IN_TEXT: Record<string, string> = {
  project: 'the project’s AI settings',
  organization: 'the organization’s AI settings',
  platform: 'the platform’s defaults',
};

interface Card {
  model: string | null;
  roles: ModelRole[];
}

/** One card per distinct model, in the kinds' order; a kind whose model is unknown is a card of its own. */
export function modelCards(roles: { role: string; model: string | null }[] | null): Card[] {
  const cards: Card[] = [];
  for (const { value } of MODEL_ROLES) {
    const model = roles?.find((r) => r.role === value)?.model ?? null;
    const same = model ? cards.find((c) => c.model === model) : undefined;
    if (same) same.roles.push(value);
    else cards.push({ model, roles: [value] });
  }
  return cards;
}

const roleHelp = (r: ModelRole) => MODEL_ROLES.find((m) => m.value === r)?.help ?? '';

export function ModelTab(ctx: AgentTabContext) {
  const { agent } = ctx;
  const host = useAgentsHost();
  const k = useAgentsKey();
  const invalidate = useInvalidate();
  const projectAgent = agent.kind === 'project';
  const full = useAgentsQuery(k('agent', agent.key), () => host.client.get(agent.key), {
    enabled: projectAgent,
  });
  const port = host.models;
  const resolved = useAgentsQuery([...host.cache.agents, 'agent-models'], () => port!.resolve(), {
    enabled: !!port,
  });
  const suites = useAgentsQuery(evalsKey(host.cache.agents, 'suites'), () => host.evals!.suites(), {
    enabled: !!host.evals && projectAgent,
  });
  const [draft, setDraft] = React.useState<ModelRole | null>(null);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState('');
  const [note, setNote] = React.useState('');
  // A platform agent without a kind of its own runs on the chat's pick, Standard by default.
  const own = projectAgent ? null : (agent.model.role as ModelRole | null);
  const saved = (
    projectAgent ? (full.data?.modelRole ?? agent.model.role ?? 'standard') : (own ?? 'standard')
  ) as ModelRole;
  const role = draft ?? saved;
  const cards = modelCards(port ? (resolved.data?.roles ?? null) : null);
  const distinct = cards.filter((c) => c.model).length;
  const canEdit = projectAgent && !!full.data?.canManage && !busy;
  const pick = (c: Card) => {
    setNote('');
    const next = c.roles.includes(saved) ? saved : c.roles[0]!;
    setDraft(next === saved ? null : next);
  };
  const save = async () => {
    if (!draft) return;
    setBusy(true);
    setError('');
    try {
      await host.client.save(agent.key, { modelRole: draft });
      setDraft(null);
      setNote(`Saved as one change. The next run uses it.`);
      invalidate([k()]);
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setBusy(false);
    }
  };
  const settings = port?.settingsPath?.({ setIn: 'project' }) ?? null;
  const from = resolved.data?.roles.find((r) => r.role === role)?.setIn;
  const hasCases = (suites.data ?? []).some(
    (s) => s.subject === `agent:${agent.key}` && s.cases.some((c) => c.enabled !== false),
  );
  const pickedModel = cards.find((c) => c.roles.includes(role))?.model ?? null;

  return (
    <div className="flex flex-col gap-3" data-testid="agent-tab-model-role">
      <div className="text-xs text-page-muted" data-testid="agent-model-lead">
        {!projectAgent && !own
          ? 'It runs on the model picked in the chat; Standard if none.'
          : 'Which model answers for this agent.'}
        {port && resolved.data && distinct < MODEL_ROLES.length
          ? ` This project has ${distinct} ${distinct === 1 ? 'model' : 'models'} for its ${MODEL_ROLES.length} kinds.`
          : ''}
      </div>
      {port && resolved.loading && !resolved.data ? (
        <div className="text-sm text-page-muted">Reading the project’s AI settings…</div>
      ) : null}
      {port && !resolved.data && resolved.error ? (
        <div className="text-xs text-page-muted">The models could not be read: {resolved.error}</div>
      ) : null}
      <div
        className="grid gap-3"
        style={{ gridTemplateColumns: `repeat(auto-fit, minmax(220px, 1fr))` }}
        data-testid="agent-model-cards"
      >
        {cards.map((c) => {
          const on = c.roles.includes(role);
          const now = c.roles.includes(saved);
          const id = c.roles.join('-');
          return (
            <label
              key={id}
              className={cn(
                'rounded-md border bg-card-bg p-3 text-sm',
                on ? 'border-nav-active ring-1 ring-inset ring-nav-active' : 'border-card-border',
                canEdit ? 'cursor-pointer' : '',
                !canEdit && !on ? 'opacity-70' : '',
              )}
              data-testid={`agent-model-card-${id}`}
              data-on={String(on)}
            >
              <div className="flex items-center gap-2">
                {projectAgent ? (
                  <input
                    type="radio"
                    name="agent-model"
                    className="m-0"
                    checked={on}
                    disabled={!canEdit}
                    onChange={() => pick(c)}
                  />
                ) : null}
                <span className={cn('font-semibold text-page-title', c.model ? 'font-mono text-[13px]' : '')}>
                  {c.model ?? c.roles.map(roleLabel).join(' · ')}
                </span>
                {now ? (
                  <span className="ml-auto rounded border border-nav-active px-1.5 text-[11px] text-nav-active">
                    {projectAgent || own ? 'now' : 'chat default'}
                  </span>
                ) : null}
              </div>
              {c.model ? (
                <div className="mt-1 text-xs text-page-muted">{c.roles.map(roleLabel).join(' · ')}</div>
              ) : null}
              <div className="mt-1 text-xs text-page-muted">{c.roles.map(roleHelp).join(' ')}</div>
              {projectAgent && on && c.roles.length > 1 ? (
                <div className="mt-1.5 text-xs text-page-muted" data-testid="agent-model-kept">
                  Saved as <b className="text-page-text">{roleLabel(role)}</b>.
                </div>
              ) : null}
            </label>
          );
        })}
      </div>
      {error ? (
        <div className="text-sm text-red-600" data-testid="agent-model-error">
          {error}
        </div>
      ) : null}
      {note ? (
        <div className="text-sm text-page-muted" data-testid="agent-model-note">
          {note}
        </div>
      ) : null}
      {draft ? (
        <div className="flex flex-wrap items-center gap-2" data-testid="agent-model-savebar">
          <button
            type="button"
            className={btnPrimary}
            data-testid="agent-model-save"
            disabled={busy}
            onClick={() => void save()}
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : null} Save
          </button>
          <button
            type="button"
            className={btnGhost}
            data-testid="agent-model-discard"
            disabled={busy}
            onClick={() => setDraft(null)}
          >
            Discard
          </button>
          {hasCases ? (
            <span className="text-xs text-page-muted" data-testid="agent-model-try">
              Not sure?{' '}
              <Link
                className="text-nav-active hover:underline"
                to={host.paths.agent(agent.key, `tab=evaluations&role=${draft}`)}
              >
                Run its evaluations with {pickedModel ?? roleLabel(draft)} first ›
              </Link>
            </span>
          ) : null}
        </div>
      ) : null}
      {port ? (
        <div className="text-xs text-page-muted" data-testid="agent-model-where">
          Models for each kind are set in{' '}
          {settings ? (
            <Link
              to={settings}
              className="text-nav-active hover:underline"
              data-testid="agent-model-settings-link"
            >
              the project’s AI settings ›
            </Link>
          ) : (
            'the project’s AI settings'
          )}
          {resolved.data?.source === 'own' && resolved.data.provider
            ? ` (now the organization’s own provider “${resolved.data.provider}”)`
            : from && from !== 'project'
              ? ` (now from ${SET_IN_TEXT[from] ?? from})`
              : ''}
          .
        </div>
      ) : null}
      {agent.access || agent.companion || agent.context ? (
        <div className="flex flex-col gap-0.5 text-xs text-page-muted" data-testid="agent-model-persona">
          {agent.access ? <div>Access: {agent.access}</div> : null}
          {agent.companion ? <div>Offered when the app turns on: {agent.companion}</div> : null}
          {agent.context ? <div>Starts from: {agent.context}</div> : null}
        </div>
      ) : null}
    </div>
  );
}
